import type { UserBlockReason } from '@masters-club/types';

/**
 * Tag attached to a user (краткий формат из backend)
 */
export interface UserTag {
  id: number;
  name: string;
  color: string;
}

/**
 * User entity from backend
 */
export interface User {
  id: number;
  telegramId: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  email?: string | null;
  isEmailVerified?: boolean;
  phone: string | null;
  createdAt: string;
  updatedAt: string;
  /** Код предпочитаемого языка интерфейса (например "ru", "en", "uz"). */
  languagePreference?: string;
  /** Код предпочитаемой валюты (ISO-4217: USD, UZS, RUB). */
  currencyPreference?: string | null;
  /** Таймзона (IANA, например "Asia/Tashkent"). */
  timezone?: string | null;
  /** Активность аккаунта (false = логически отключён). */
  isActive?: boolean;
  /** ID выбранной страны (FK на Country). */
  countryId?: number | null;
  /** ISO-2 код последней geo-страны (по IP). */
  lastCountry?: string | null;
  /** Развёрнутая выбранная страна (загружена backend'ом). */
  country?: { id: number; code: string; name: string } | null;
  /** LTV в базовой валюте аналитики. */
  totalSpentBase?: number;
  /** Количество подтверждённых платежей. */
  purchaseCount?: number;
  /** Дата первого платежа. */
  firstPaymentAt?: string | null;
  /** Дата последнего платежа. */
  lastPaymentAt?: string | null;
  /** Канал привлечения (direct/bot/referral/utm_*). */
  acquisitionChannel?: string | null;
  subscriptions: {
    id: string;
    status: string;
    planCode: string | null;
    startDate: string | null;
    endDate: string | null;
    isFamilyOwner?: boolean;
    currentFamilySlots?: number;
  }[];
  _count?: {
    payments: number;
    chats: number;
  };
  accessibleCourses: string[];
  accessibleChannels: string[];
  /** ISO timestamp блокировки аккаунта админом. null/undefined — не заблокирован. */
  blockedAt?: string | null;
  /** ID админа, выполнившего блокировку. */
  blockedBy?: number | null;
  /** Человекочитаемая причина блокировки (3..500 символов). */
  blockReason?: string | null;
  /** Машиночитаемый код причины блокировки. */
  blockReasonCode?: UserBlockReason | null;
  /** ISO timestamp soft-delete аккаунта. */
  deletedAt?: string | null;
  /** Теги, назначенные пользователю. */
  tags: UserTag[];
  /** UTM-атрибуция: источник трафика. */
  utmSource: string | null;
  /** UTM-атрибуция: канал трафика. */
  utmMedium: string | null;
  /** UTM-атрибуция: кампания. */
  utmCampaign: string | null;
  /** UTM-атрибуция: ключевое слово. */
  utmTerm: string | null;
  /** UTM-атрибуция: содержание объявления. */
  utmContent: string | null;
}

/**
 * User update payload (limited fields)
 */
export interface UserUpdatePayload {
  phone?: string;
}

/**
 * Payload для POST /admin/users/:id/block
 */
export interface BlockUserPayload {
  reason: string;
  reasonCode: UserBlockReason;
}

/**
 * Локализованные лейблы для кодов причин блокировки.
 * Источник правды значений — enum UserBlockReason в @masters-club/types.
 */
export const BLOCK_REASON_LABELS: Record<UserBlockReason, string> = {
  SPAM: 'Спам',
  ABUSE: 'Оскорбления/агрессия',
  FRAUD: 'Мошенничество',
  PAYMENT_CHARGEBACK: 'Чарджбэк',
  TOS_VIOLATION: 'Нарушение правил',
  OTHER: 'Другое',
};

/**
 * Получить статус учётной записи для отображения в UI.
 */
export function getUserAccountStatus(user: Pick<User, 'blockedAt' | 'deletedAt'>):
  | 'active'
  | 'blocked'
  | 'deleted' {
  if (user.deletedAt) {
    return 'deleted';
  }
  if (user.blockedAt) {
    return 'blocked';
  }
  return 'active';
}

/**
 * Запись из GET /users/:id/block-history.
 * История переживает unblock — даёт полный audit-trail действий админов.
 */
export interface UserBlockHistoryEntry {
  id: number;
  action: 'BLOCK' | 'UNBLOCK';
  reason: string | null;
  reasonCode: UserBlockReason | null;
  performedBy: number | null;
  performedByName: string | null;
  performedByEmail: string | null;
  performedAt: string;
}
